/**
 * AssetLoader - Utility for loading and caching assets
 * Handles images, JSON configs, and fonts with progress tracking
 */

export class AssetLoader {
  constructor() {
    this.cache = new Map();
    this.loadingPromises = new Map();
  }

  /**
   * Load an image asset
   * @param {string} url - Image URL
   * @param {boolean} useCache - Whether to use cached version
   * @returns {Promise<HTMLImageElement>}
   */
  async loadImage(url, useCache = true) {
    // Return cached image if available
    if (useCache && this.cache.has(url)) {
      console.log(`[AssetLoader] Using cached image: ${url}`);
      return this.cache.get(url);
    }

    // Return existing loading promise if already loading
    if (this.loadingPromises.has(url)) {
      console.log(`[AssetLoader] Waiting for existing load: ${url}`);
      return this.loadingPromises.get(url);
    }

    // Create new loading promise
    const loadPromise = new Promise((resolve, reject) => {
      const img = new Image();

      img.onload = () => {
        console.log(`[AssetLoader] ✓ Image loaded: ${url}`);
        if (useCache) {
          this.cache.set(url, img);
        }
        this.loadingPromises.delete(url);
        resolve(img);
      };

      img.onerror = (error) => {
        console.error(`[AssetLoader] ✗ Failed to load image: ${url}`, error);
        this.loadingPromises.delete(url);
        reject(new Error(`Failed to load image: ${url}`));
      };

      img.src = url;
    });

    this.loadingPromises.set(url, loadPromise);
    return loadPromise;
  }

  /**
   * Load a JSON config file
   * @param {string} url - JSON file URL
   * @param {boolean} useCache - Whether to use cached version
   * @returns {Promise<Object>}
   */
  async loadJSON(url, useCache = true) {
    // Return cached JSON if available
    if (useCache && this.cache.has(url)) {
      console.log(`[AssetLoader] Using cached JSON: ${url}`);
      return this.cache.get(url);
    }

    // Return existing loading promise if already loading
    if (this.loadingPromises.has(url)) {
      console.log(`[AssetLoader] Waiting for existing load: ${url}`);
      return this.loadingPromises.get(url);
    }

    // Create new loading promise
    const loadPromise = fetch(url)
      .then(response => {
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        return response.json();
      })
      .then(data => {
        console.log(`[AssetLoader] ✓ JSON loaded: ${url}`);
        if (useCache) {
          this.cache.set(url, data);
        }
        this.loadingPromises.delete(url);
        return data;
      })
      .catch(error => {
        console.error(`[AssetLoader] ✗ Failed to load JSON: ${url}`, error);
        this.loadingPromises.delete(url);
        throw new Error(`Failed to load JSON: ${url} - ${error.message}`);
      });

    this.loadingPromises.set(url, loadPromise);
    return loadPromise;
  }

  /**
   * Load multiple assets in parallel with progress tracking
   * @param {Array<{type: string, url: string}>} assets - Array of assets to load
   * @param {Function} onProgress - Progress callback (loaded, total)
   * @returns {Promise<Array>}
   */
  async loadMultiple(assets, onProgress = null) {
    console.log(`[AssetLoader] Loading ${assets.length} assets...`);

    let loaded = 0;
    const total = assets.length;

    const loadPromises = assets.map(async (asset) => {
      try {
        let result;

        switch (asset.type) {
          case 'image':
            result = await this.loadImage(asset.url);
            break;
          case 'json':
            result = await this.loadJSON(asset.url);
            break;
          default:
            throw new Error(`Unknown asset type: ${asset.type}`);
        }

        loaded++;
        if (onProgress) {
          onProgress(loaded, total);
        }

        return { url: asset.url, data: result, success: true };
      } catch (error) {
        loaded++;
        if (onProgress) {
          onProgress(loaded, total);
        }

        return { url: asset.url, error: error.message, success: false };
      }
    });

    const results = await Promise.all(loadPromises);

    // Check for failures
    const failures = results.filter(r => !r.success);
    if (failures.length > 0) {
      console.warn(`[AssetLoader] ${failures.length} assets failed to load:`, failures);
    }

    console.log(`[AssetLoader] ✓ Loaded ${results.filter(r => r.success).length}/${total} assets`);
    return results;
  }

  /**
   * Load a font
   * @param {string} fontFamily - Font family name
   * @param {string} url - Font file URL
   * @param {Object} descriptors - FontFace descriptors (weight, style, etc.)
   * @returns {Promise<FontFace>}
   */
  async loadFont(fontFamily, url, descriptors = {}) {
    const cacheKey = `font:${fontFamily}:${url}`;

    // Return cached font if available
    if (this.cache.has(cacheKey)) {
      console.log(`[AssetLoader] Using cached font: ${fontFamily}`);
      return this.cache.get(cacheKey);
    }

    try {
      console.log(`[AssetLoader] Loading font: ${fontFamily} from ${url}`);

      const fontFace = new FontFace(fontFamily, `url(${url})`, descriptors);
      const loadedFont = await fontFace.load();

      // Add font to document
      document.fonts.add(loadedFont);

      console.log(`[AssetLoader] ✓ Font loaded: ${fontFamily}`);
      this.cache.set(cacheKey, loadedFont);

      return loadedFont;
    } catch (error) {
      console.error(`[AssetLoader] ✗ Failed to load font: ${fontFamily}`, error);
      throw new Error(`Failed to load font: ${fontFamily} - ${error.message}`);
    }
  }

  /**
   * Preload user-uploaded file as data URL
   * @param {File} file - File object
   * @returns {Promise<string>} Data URL
   */
  async loadFile(file) {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();

      reader.onload = (e) => {
        console.log(`[AssetLoader] ✓ File loaded: ${file.name}`);
        resolve(e.target.result);
      };

      reader.onerror = (error) => {
        console.error(`[AssetLoader] ✗ Failed to load file: ${file.name}`, error);
        reject(new Error(`Failed to load file: ${file.name}`));
      };

      reader.readAsDataURL(file);
    });
  }

  /**
   * Check if an asset is cached
   * @param {string} url - Asset URL
   * @returns {boolean}
   */
  isCached(url) {
    return this.cache.has(url);
  }

  /**
   * Get cached asset
   * @param {string} url - Asset URL
   * @returns {*}
   */
  getCached(url) {
    return this.cache.get(url);
  }

  /**
   * Clear specific asset from cache
   * @param {string} url - Asset URL
   */
  clearCache(url) {
    if (url) {
      this.cache.delete(url);
      console.log(`[AssetLoader] Cache cleared for: ${url}`);
    } else {
      this.cache.clear();
      console.log('[AssetLoader] All cache cleared');
    }
  }

  /**
   * Get cache statistics
   * @returns {{size: number, urls: Array<string>}}
   */
  getCacheStats() {
    return {
      size: this.cache.size,
      urls: Array.from(this.cache.keys())
    };
  }
}

// Create singleton instance
export const assetLoader = new AssetLoader();

console.log('AssetLoader loaded');
